import React, { Component, type ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import posthog from 'posthog-js'

import App from './App'
import './index.css'

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RootErrorBoundary] Caught render error:', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'monospace', background: '#fff', minHeight: '100vh' }}>
          <h1 style={{ color: '#dc2626' }}>Something went wrong</h1>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '1rem', color: '#374151' }}>
            {(this.state.error as Error).message}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '1rem', color: '#6b7280', fontSize: '0.75rem' }}>
            {(this.state.error as Error).stack}
          </pre>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * PostHog initialization.
 *
 * Must run BEFORE React mounts so that the posthog instance is ready
 * for any useEffect calls that fire on first render.
 *
 * In development mode we opt out of capturing to avoid polluting analytics
 * with local test data.
 */
const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host:
      (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
      'https://app.posthog.com',
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.opt_out_capturing()
      }
    },
  })
}

/**
 * TanStack Query client.
 *
 * Default staleTime of 60 s means most GET responses are served from cache
 * within a minute, reducing redundant network requests during navigation.
 * Retry is set to 1 to avoid hammering a temporarily unavailable backend.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found in the DOM.')
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
)
